import { db } from '../config/firebase.js';
import { collection, addDoc, query, where, getDocs, orderBy, Timestamp, doc, getDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

export const ACTIVITY_TYPES = {
  // Document activities
  DOCUMENT_CREATED: 'document_created',
  DOCUMENT_UPDATED: 'document_updated',
  DOCUMENT_DELETED: 'document_deleted',
  DOCUMENT_SHARED: 'document_shared',
  DOCUMENT_VIEWED: 'document_viewed',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  DOCUMENT_UPLOADED: 'document_uploaded',
  DOCUMENT_STATUS_CHANGED: 'document_status_changed',
  
  // User activities
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  USER_SESSION_EXPIRED: 'user_session_expired',
  USER_PASSWORD_CHANGED: 'user_password_changed',
  
  // Role and permissions
  ROLE_CHANGED: 'role_changed',
  PERMISSIONS_UPDATED: 'permissions_updated',
  SETTINGS_UPDATED: 'settings_updated',
  
  // Incident core activities
  INCIDENT_CREATED: 'incident_created',
  INCIDENT_UPDATED: 'incident_updated',
  INCIDENT_DELETED: 'incident_deleted',
  INCIDENT_STATUS_CHANGED: 'incident_status_changed',
  INCIDENT_SEVERITY_CHANGED: 'incident_severity_changed',
  INCIDENT_LOCATION_CHANGED: 'incident_location_changed',
  INCIDENT_OCCURRENCE_TIME_CHANGED: 'incident_occurrence_time_changed',
  INCIDENT_ASSIGNED: 'incident_assigned',
  INCIDENT_CLOSED: 'incident_closed',
  INCIDENT_REOPENED: 'incident_reopened',
  
  // Task activities
  TASK_CREATED: 'task_created',
  TASK_UPDATED: 'task_updated',
  TASK_DELETED: 'task_deleted',
  TASK_STATUS_CHANGED: 'task_status_changed',
  TASK_ASSIGNED: 'task_assigned',
  TASK_UNASSIGNED: 'task_unassigned',
  TASK_PRIORITY_CHANGED: 'task_priority_changed',
  TASK_DUE_DATE_CHANGED: 'task_due_date_changed',
  TASK_COMPLETED: 'task_completed',
  TASK_DEPENDENCY_ADDED: 'task_dependency_added',
  TASK_DEPENDENCY_REMOVED: 'task_dependency_removed',
  
  // Milestone activities
  MILESTONE_CREATED: 'milestone_created',
  MILESTONE_UPDATED: 'milestone_updated',
  MILESTONE_DELETED: 'milestone_deleted',
  MILESTONE_STATUS_CHANGED: 'milestone_status_changed',
  MILESTONE_MOVED: 'milestone_moved',
  MILESTONE_COMPLETED: 'milestone_completed',
  MILESTONE_DUE_DATE_CHANGED: 'milestone_due_date_changed',
  
  // Timeline activities
  TIMELINE_ITEM_CREATED: 'timeline_item_created',
  TIMELINE_ITEM_MOVED: 'timeline_item_moved',
  TIMELINE_ITEM_UPDATED: 'timeline_item_updated',
  TIMELINE_ITEM_DELETED: 'timeline_item_deleted',
  
  // Regulatory activities
  REGULATORY_TRIGGERS_UPDATED: 'regulatory_triggers_updated',
  REGULATORY_TASK_CREATED: 'regulatory_task_created',
  REGULATORY_DEADLINE_CREATED: 'regulatory_deadline_created',
  REGULATORY_DEADLINE_UPDATED: 'regulatory_deadline_updated',
  REGULATORY_COMPLIANCE_CHECKED: 'regulatory_compliance_checked',
  
  // Staff activities
  STAFF_UPDATED: 'staff_updated',
  STAFF_ADDED: 'staff_added',
  STAFF_REMOVED: 'staff_removed',
  STAFF_STATUS_CHANGED: 'staff_status_changed',
  STAFF_MANUAL_ENTRY: 'staff_manual_entry',
  STAFF_LOCATION_UPDATED: 'staff_location_updated',
  STAFF_BULK_UPDATE: 'staff_bulk_update',
  STAFF_SAFETY_CHECK: 'staff_safety_check',
  STAFF_NEED_ASSISTANCE: 'staff_need_assistance',
  
  // Communication activities
  MESSAGE_SENT: 'message_sent',
  MESSAGE_RECEIVED: 'message_received',
  MESSAGE_EDITED: 'message_edited',
  MESSAGE_DELETED: 'message_deleted',
  LIVE_CHAT_MESSAGE_CREATED: 'live_chat_message_created',
  LIVE_CHAT_MESSAGE_EDITED: 'live_chat_message_edited',
  LIVE_CHAT_MESSAGE_DELETED: 'live_chat_message_deleted',
  SYSTEM_CHAT_MESSAGE_SENT: 'system_chat_message_sent',
  MESSAGE_SCHEDULED: 'message_scheduled',
  MESSAGE_TEMPLATE_CREATED: 'message_template_created',
  SMS_SENT: 'sms_sent',
  SMS_RECEIVED: 'sms_received',
  SMS_REPLY_RECEIVED: 'sms_reply_received',
  SMS_NEED_HELP_REPLY: 'sms_need_help_reply',
  EMAIL_SENT: 'email_sent',
  
  // Media intelligence activities
  MEDIA_ARTICLE_DETECTED: 'media_article_detected',
  MEDIA_ARTICLE_STORED: 'media_article_stored',
  MEDIA_ARTICLE_FILTERED: 'media_article_filtered',
  MEDIA_ALERT_CREATED: 'media_alert_created',
  MEDIA_ALERT_RESOLVED: 'media_alert_resolved',
  MEDIA_INTELLIGENCE_RUN: 'media_intelligence_run',
  MEDIA_ARTICLE_REVIEWED: 'media_article_reviewed',
  
  // AI and automation activities
  AI_SUMMARY_GENERATED: 'ai_summary_generated',
  AI_SNAPSHOT_CREATED: 'ai_snapshot_created',
  AI_ANALYSIS_COMPLETED: 'ai_analysis_completed',
  AUTOMATION_TASK_CREATED: 'automation_task_created',
  AUTOMATION_RULE_TRIGGERED: 'automation_rule_triggered',
  
  // Update and communication activities
  INCIDENT_UPDATE_CREATED: 'incident_update_created',
  INCIDENT_UPDATE_EDITED: 'incident_update_edited',
  INCIDENT_UPDATE_DELETED: 'incident_update_deleted',
  
  // SMS Communication activities
  SMS_COMMUNICATION: 'sms_communication',
  MASS_SMS_COMMUNICATION: 'mass_sms_communication',
  
  // Lessons learned activities
  LESSONS_LEARNED_CREATED: 'lessons_learned_created',
  LESSONS_LEARNED_UPDATED: 'lessons_learned_updated',
  
  // AI snapshot activities (expanded)
  AI_SNAPSHOT_GENERATED: 'ai_snapshot_generated',
  
  // Access and security activities
  DATA_EXPORT_REQUESTED: 'data_export_requested',
  DATA_EXPORT_COMPLETED: 'data_export_completed',
  UNAUTHORIZED_ACCESS_ATTEMPT: 'unauthorized_access_attempt',
  SECURITY_BREACH_DETECTED: 'security_breach_detected',
  
  // Video Conference activities
  VIDEO_CALL_ROOM_CREATED: 'video_call_room_created',
  VIDEO_CALL_STARTED: 'video_call_started',
  VIDEO_CALL_ENDED: 'video_call_ended',
  VIDEO_CALL_PARTICIPANT_JOINED: 'video_call_participant_joined',
  VIDEO_CALL_PARTICIPANT_LEFT: 'video_call_participant_left',
  VIDEO_CALL_DURATION_LOGGED: 'video_call_duration_logged',
  VIDEO_CALL_ROOM_ACCESSED: 'video_call_room_accessed',
  
  // System activities
  SYSTEM_BACKUP_CREATED: 'system_backup_created',
  SYSTEM_MAINTENANCE: 'system_maintenance',
  DATABASE_MIGRATION: 'database_migration',
  COMMS_CHANNEL_SWITCHED: 'comms_channel_switched'
};

export const ACTIVITY_TYPE_LABELS = {
  // Document labels
  document_created: 'Document Created',
  document_updated: 'Document Updated',
  document_deleted: 'Document Deleted',
  document_shared: 'Document Shared',
  document_viewed: 'Document Viewed',
  document_downloaded: 'Document Downloaded',
  document_uploaded: 'Document Uploaded',
  document_status_changed: 'Document Status Changed',
  
  // User labels
  user_logged_in: 'User Logged In',
  user_logged_out: 'User Logged Out',
  user_created: 'User Created',
  user_updated: 'User Updated',
  user_deleted: 'User Deleted',
  user_session_expired: 'Session Expired',
  user_password_changed: 'Password Changed',
  
  // Role labels
  role_changed: 'Role Changed',
  permissions_updated: 'Permissions Updated',
  settings_updated: 'Settings Updated',
  
  // Incident labels
  incident_created: 'Incident Created',
  incident_updated: 'Incident Updated',
  incident_deleted: 'Incident Deleted',
  incident_status_changed: 'Status Changed',
  incident_severity_changed: 'Severity Changed',
  incident_location_changed: 'Location Changed',
  incident_occurrence_time_changed: 'Occurrence Time Changed',
  incident_assigned: 'Incident Assigned',
  incident_closed: 'Incident Closed',
  incident_reopened: 'Incident Reopened',
  
  // Task labels
  task_created: 'Task Created',
  task_updated: 'Task Updated',
  task_deleted: 'Task Deleted',
  task_status_changed: 'Task Status Changed',
  task_assigned: 'Task Assigned',
  task_unassigned: 'Task Unassigned',
  task_priority_changed: 'Task Priority Changed',
  task_due_date_changed: 'Task Due Date Changed',
  task_completed: 'Task Completed',
  task_dependency_added: 'Task Dependency Added',
  task_dependency_removed: 'Task Dependency Removed',
  
  // Milestone labels
  milestone_created: 'Milestone Created',
  milestone_updated: 'Milestone Updated',
  milestone_deleted: 'Milestone Deleted',
  milestone_status_changed: 'Milestone Status Changed',
  milestone_moved: 'Milestone Moved',
  milestone_completed: 'Milestone Completed',
  milestone_due_date_changed: 'Milestone Due Date Changed',
  
  // Timeline labels
  timeline_item_created: 'Timeline Item Created',
  timeline_item_moved: 'Timeline Item Moved',
  timeline_item_updated: 'Timeline Item Updated',
  timeline_item_deleted: 'Timeline Item Deleted',
  
  // Regulatory labels
  regulatory_triggers_updated: 'Regulatory Triggers Updated',
  regulatory_task_created: 'Regulatory Task Created',
  regulatory_deadline_created: 'Regulatory Deadline Created',
  regulatory_deadline_updated: 'Regulatory Deadline Updated',
  regulatory_compliance_checked: 'Compliance Checked',
  
  // Staff labels
  staff_updated: 'Staff Updated',
  staff_added: 'Staff Added',
  staff_removed: 'Staff Removed',
  staff_status_changed: 'Staff Status Changed',
  staff_manual_entry: 'Staff Manual Entry',
  staff_location_updated: 'Staff Location Updated',
  staff_bulk_update: 'Staff Bulk Update',
  staff_safety_check: 'Staff Safety Check',
  staff_need_assistance: 'Need Assistance',
  
  // Communication labels
  message_sent: 'Message Sent',
  message_received: 'Message Received',
  message_edited: 'Message Edited',
  message_deleted: 'Message Deleted',
  live_chat_message_created: 'Live Chat Message',
  live_chat_message_edited: 'Chat Message Edited',
  live_chat_message_deleted: 'Chat Message Deleted',
  system_chat_message_sent: 'System Message Sent',
  message_scheduled: 'Message Scheduled',
  message_template_created: 'Message Template Created',
  sms_sent: 'SMS Sent',
  sms_received: 'SMS Received',
  sms_reply_received: 'SMS Reply Received',
  sms_need_help_reply: 'SMS Need Help Reply',
  email_sent: 'Email Sent',
  
  // Media intelligence labels
  media_article_detected: 'Media Article Detected',
  media_article_stored: 'Media Article Stored',
  media_article_filtered: 'Media Article Filtered',
  media_alert_created: 'Media Alert Created',
  media_alert_resolved: 'Media Alert Resolved',
  media_intelligence_run: 'Media Intelligence Run',
  media_article_reviewed: 'Media Article Reviewed',
  
  // AI labels
  ai_summary_generated: 'AI Summary Generated',
  ai_snapshot_created: 'AI Snapshot Created',
  ai_analysis_completed: 'AI Analysis Completed',
  automation_task_created: 'Automation Task Created',
  automation_rule_triggered: 'Automation Rule Triggered',
  
  // Update labels
  incident_update_created: 'Incident Update Created',
  incident_update_edited: 'Incident Update Edited',
  incident_update_deleted: 'Incident Update Deleted',
  
  // SMS Communication labels
  sms_communication: 'SMS Communication',
  mass_sms_communication: 'Mass SMS Communication',
  
  // Security labels
  data_export_requested: 'Data Export Requested',
  data_export_completed: 'Data Export Completed',
  unauthorized_access_attempt: 'Unauthorized Access Attempt',
  security_breach_detected: 'Security Breach Detected',
  
  // Video Conference labels
  video_call_room_created: 'Video Call Room Created',
  video_call_started: 'Video Call Started',
  video_call_ended: 'Video Call Ended',
  video_call_participant_joined: 'Participant Joined Call',
  video_call_participant_left: 'Participant Left Call',
  video_call_duration_logged: 'Video Call Duration Recorded',
  video_call_room_accessed: 'Video Call Room Accessed',
  
  // System labels
  system_backup_created: 'System Backup Created',
  system_maintenance: 'System Maintenance',
  database_migration: 'Database Migration',
  comms_channel_switched: 'Comms Channel Switched',
  
  // Lessons learned labels
  lessons_learned_created: 'Lessons Learned Created',
  lessons_learned_updated: 'Lessons Learned Updated',
  
  // AI snapshot labels
  ai_snapshot_generated: 'AI Snapshot Generated'
};

/**
 * Get user's full name for legal-grade documentation
 * This is CRITICAL for forensic audit trails
 */
export async function getUserFullName(db, userId, fallbackEmail = null, existingUserData = null) {
  try {
    // If we already have user data, use it
    if (existingUserData) {
      const fullName = `${existingUserData.firstName || ''} ${existingUserData.lastName || ''}`.trim();
      if (fullName) return fullName;
      if (existingUserData.displayName) return existingUserData.displayName;
    }

    // Fetch from database if needed
    if (userId && userId !== 'System' && userId !== 'system') {
      const userRef = doc(db, 'c3_users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
        if (fullName) return fullName;
        if (userData.displayName) return userData.displayName;
      }
    }

    // Fallbacks for system or missing users
    if (userId === 'System' || userId === 'system') return 'System';
    if (fallbackEmail) return fallbackEmail;
    return 'Unknown User';
    
  } catch (error) {
    console.error('Error getting user full name:', error);
    return fallbackEmail || 'Unknown User';
  }
}

export async function logActivity({
  type,
  userId,
  userEmail,
  organisationId,
  details,
  metadata = {},
  userFullName = null,
  ipAddress = null,
  sessionId = null
}) {
  try {
    // Ensure we have user's full name for legal documentation
    const resolvedFullName = userFullName || await getUserFullName(db, userId, userEmail, metadata.userData);

    const logEntry = {
      type,
      userId: userId || 'System',
      userEmail: userEmail || 'system@c3.com',
      userFullName: resolvedFullName, // CRITICAL: Always include full name
      orgId: organisationId,
      details,
      metadata: {
        ...metadata,
        ipAddress,
        sessionId,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : null,
        timestamp: Date.now()
      },
      timestamp: Timestamp.now(),
      isEditable: false, // Forensic requirement: logs are immutable
      auditLevel: 'standard' // Can be 'standard', 'detailed', 'forensic'
    };

    // Always write to audit logs for admin trail
    await addDoc(collection(db, 'c3_auditLogs'), logEntry);
    
    // ALWAYS write to incident logs for dashboard real-time updates 
    // (even non-incident activities for Last Update tracking)
    const incidentLogEntry = {
      ...logEntry,
      incidentId: metadata.incidentId || null, // Allow null for non-incident activities
      action: type, // IncidentLogTab expects 'action' field
      priority: metadata.priority || 'normal'
    };
    
    await addDoc(collection(db, 'c3_incident_logs'), incidentLogEntry);
    console.log(`📋 Incident log created for dashboard tracking: ${type} by ${resolvedFullName}`);

    // Also log incident-specific activities
    if (metadata.incidentId) {
      console.log(`📋 Incident-specific log: ${type} for incident ${metadata.incidentId}`);
    }

    console.log(`📋 Forensic audit log created: ${type} by ${resolvedFullName}`);
    return true;
  } catch (error) {
    console.error('Error logging activity:', error);
    // Log the error to a separate error collection for monitoring
    try {
      await addDoc(collection(db, 'c3_audit_errors'), {
        error: error.message,
        stack: error.stack,
        timestamp: Timestamp.now(),
        activityType: type,
        userId,
        orgId: organisationId
      });
    } catch (logError) {
      console.error('Failed to log audit error:', logError);
    }
    throw error;
  }
}

/**
 * Create incident-specific log entry with enhanced forensic formatting
 * This ensures all critical incident events are properly recorded for legal purposes
 */
export async function createIncidentLog({
  incidentId,
  orgId,
  action,
  details,
  userId,
  userEmail,
  userFullName,
  priority = 'normal',
  metadata = {},
  ipAddress = null,
  sessionId = null
}) {
  try {
    if (!orgId || !action || !details) {
      throw new Error('Missing required fields for incident log');
    }
    
    // Allow null incidentId for dashboard activity tracking
    if (incidentId === null) {
      console.log('📊 Creating dashboard activity log (no incident context)');
    }

    // Ensure we have user's full name for legal documentation
    const resolvedFullName = userFullName || await getUserFullName(
      db, 
      userId || 'System', 
      userEmail || 'system@c3.com',
      metadata.userData
    );

    const logEntry = {
      incidentId: incidentId || null, // Explicitly handle null for dashboard tracking
      orgId,
      action,
      details,
      userId: userId || 'System',
      userEmail: userEmail || 'system@c3.com',
      userFullName: resolvedFullName, // CRITICAL: Always include full name for legal docs
      priority,
      metadata: {
        ...metadata,
        incidentId: incidentId || null, // Ensure incidentId is properly set in metadata
        ipAddress,
        sessionId,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : null,
        timestamp: Date.now(),
        auditTrail: true, // Mark as part of forensic audit trail
        dashboardTracking: incidentId === null // Flag for dashboard-only activities
      },
      timestamp: Timestamp.now(),
      isEditable: false, // Forensic requirement: logs are immutable
      auditLevel: incidentId ? 'forensic' : 'dashboard' // Dashboard activities are lower priority
    };

    // Write to incident logs (primary location for incident log tab)
    await addDoc(collection(db, 'c3_incident_logs'), logEntry);
    
    // Also write to audit logs for admin trail
    await addDoc(collection(db, 'c3_auditLogs'), {
      type: action,
      userId: userId || 'System',
      userEmail: userEmail || 'system@c3.com',
      userFullName: resolvedFullName,
      orgId,
      details,
      metadata: logEntry.metadata,
      timestamp: logEntry.timestamp,
      isEditable: false,
      auditLevel: 'forensic'
    });

    console.log(`📋 Forensic incident log created: ${action} for incident ${incidentId} by ${resolvedFullName}`);
    return true;
  } catch (error) {
    console.error('Error creating incident log:', error);
    
    // Log the error to monitoring system
    try {
      await addDoc(collection(db, 'c3_audit_errors'), {
        error: error.message,
        stack: error.stack,
        timestamp: Timestamp.now(),
        action,
        incidentId,
        userId,
        orgId,
        type: 'incident_log_error'
      });
    } catch (logError) {
      console.error('Failed to log incident audit error:', logError);
    }
    
    throw error;
  }
}

export async function getActivityLogs({
  organisationId,
  startDate,
  endDate,
  types = [],
  userId = null
}) {
  try {
    let q = query(
      collection(db, 'c3_auditLogs'),
      where('orgId', '==', organisationId),
      orderBy('timestamp', 'desc')
    );

    if (startDate) {
      q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
    }

    if (endDate) {
      q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
    }

    if (types.length > 0) {
      q = query(q, where('type', 'in', types));
    }

    if (userId) {
      q = query(q, where('userId', '==', userId));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    throw error;
  }
}

// Helper function to get activity type label
export const getActivityTypeLabel = (activityType) => {
  return ACTIVITY_TYPE_LABELS[activityType] || activityType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Export log data to PDF with enhanced formatting
export const exportLogsToPDF = async (logs, incidentTitle = 'Incident', metadata = {}) => {
  try {
    const doc = new jsPDF();
    
    // Add title and security notice
    doc.setFontSize(16);
    doc.text('Activity Log Report', 14, 15);
    
    // Add security notice
    doc.setFontSize(10);
    doc.setTextColor(128, 0, 0); // Dark red for security notice
    doc.text('SECURITY NOTICE: This is an official audit log. Do not modify.', 14, 25);
    doc.setTextColor(0, 0, 0); // Reset to black
    
    // Add date range and metadata
    doc.setFontSize(10);
    const reportMetadata = [
      `Report Title: ${incidentTitle}`,
      `Generated: ${new Date().toLocaleString()}`,
      `Total Entries: ${logs.length}`
    ];
    
    if (metadata.startDate) {
      reportMetadata.unshift(`Period Start: ${new Date(metadata.startDate).toLocaleDateString()}`);
    }
    if (metadata.endDate) {
      reportMetadata.unshift(`Period End: ${new Date(metadata.endDate).toLocaleDateString()}`);
    }
    if (metadata.organisationId) {
      reportMetadata.push(`Organisation ID: ${metadata.organisationId}`);
    }
    
    doc.text(reportMetadata, 14, 35);

    // Prepare table data with enhanced formatting
    const tableData = logs.map(log => [
      log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : new Date(log.timestamp).toLocaleString(),
      getActivityTypeLabel(log.action || log.type),
      log.details || '',
      log.userFullName || log.userId || 'Unknown',
      log.orgId || log.organisationId || ''
    ]);

    // Add table with enhanced styling
    doc.autoTable({
      startY: 55,
      head: [['Timestamp', 'Activity Type', 'Details', 'User', 'Organisation ID']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [57, 50, 91], // Using our theme color
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 30 }, // Timestamp
        1: { cellWidth: 25 }, // Activity Type
        2: { cellWidth: 60 }, // Details
        3: { cellWidth: 30 }, // User
        4: { cellWidth: 30 }  // Organisation ID
      }
    });

    // Add footer with security features
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      
      // Add page number
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
      
      // Add security footer
      doc.setTextColor(128, 0, 0);
      doc.text(
        'This document is digitally generated and should not be modified. Any modifications will invalidate its authenticity.',
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 5,
        { align: 'center' }
      );
    }

    // Add digital signature
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Digital Signature: ${new Date().toISOString()}`,
      doc.internal.pageSize.width - 20,
      doc.internal.pageSize.height - 20,
      { align: 'right' }
    );

    return doc;
  } catch (error) {
    console.error('Error exporting logs to PDF:', error);
    throw error;
  }
} 